/**
 * /payroll-l2/ot-rules — OT Rule Set configuration.
 * Shows all Federal and state-specific OT rule sets.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { api } from "@/lib/api";
import type { OTRuleSet } from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l2/ot-rules")({
  component: OTRulesPage,
});

function OTRulesPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["payroll-l2", "ot-rule-sets"],
    queryFn: async () => {
      return await api<OTRuleSet[]>("/api/payroll-l2/ot-rule-sets");
    },
  });

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={600}>OT Rules</Typography>
        <HelpHint docPath="payroll-l2/ot-rules.md" label="OT rules help" />
      </Stack>

      {isLoading && <CircularProgress size={24} />}
      {error && <Alert severity="error">Failed to load OT rule sets.</Alert>}

      {data && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Daily OT Threshold</TableCell>
              <TableCell>Daily DT Threshold</TableCell>
              <TableCell>Weekly OT</TableCell>
              <TableCell>7th Day OT</TableCell>
              <TableCell>OT Mult</TableCell>
              <TableCell>DT Mult</TableCell>
              <TableCell>Active</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ fontWeight: "medium" }}>{r.name}</TableCell>
                <TableCell>{r.state_code ? <Chip label={r.state_code} size="small" /> : <span className="text-slate-400">Federal</span>}</TableCell>
                <TableCell>{r.daily_ot_threshold}h</TableCell>
                <TableCell>{r.daily_dt_threshold ? `${r.daily_dt_threshold}h` : "—"}</TableCell>
                <TableCell>{r.weekly_ot_threshold}h</TableCell>
                <TableCell>
                  <Chip
                    label={r.seventh_day_ot ? "Yes" : "No"}
                    color={r.seventh_day_ot ? "warning" : "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell>{r.ot_multiplier}×</TableCell>
                <TableCell>{r.dt_multiplier}×</TableCell>
                <TableCell>{r.is_active ? "Yes" : "No"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
